import { PropsWithChildren, useEffect, useState } from 'react'

import { useLoaderContext } from '#/contexts/Loader'
import {
  hasBypassedBrowserGuard,
  setBrowserGuardBypassed,
} from '#/store/services/BrowserGuard/Logic/browserGuardBypass'
import { validateBrowserCompatibility } from '#/store/services/BrowserGuard/Logic/browserValidator'
import {
  buildDetectedBrowser,
  buildMinimumBrowser,
  buildSentryReason,
  canBypassBrowserGuard,
  getIconButtonBrowsers,
  getMatchedBrowser,
  getMessageKeys,
  getMessageVariant,
  getSystemUpdateButton,
} from '#/store/services/BrowserGuard/Logic/unsupportedBrowser'
import { UnsupportedBrowserView } from '#/store/services/BrowserGuard/View'
import {
  SAFARI_BROWSER,
  SUPPORTED_BROWSERS,
} from '#/store/services/BrowserGuard/View/supportedBrowsers'
import { Sentry, SentryEvent } from '#/store/services/Sentry'

export function BrowserGuard({ children }: PropsWithChildren) {
  const [validationResult] = useState(() => validateBrowserCompatibility())
  const [isBypassed, setIsBypassed] = useState(() => hasBypassedBrowserGuard())

  const {
    browserName,
    browserVersion,
    isSupported,
    minimumBrowserName,
    minimumBrowserVersion,
    osName,
    osVersion,
    updateTarget,
  } = validationResult

  const detectedBrowser = buildDetectedBrowser({ browserName, browserVersion })
  const minimumBrowser = buildMinimumBrowser({ minimumBrowserName, minimumBrowserVersion })
  const matchedBrowser = getMatchedBrowser({
    browserName,
    supportedBrowsers: SUPPORTED_BROWSERS,
  })
  const messageVariant = getMessageVariant({ minimumBrowser, updateTarget })
  const messageKeys = getMessageKeys({ messageVariant })
  const systemUpdateButton = getSystemUpdateButton({ updateTarget })
  const canBypass = canBypassBrowserGuard({ matchedBrowser, messageVariant })
  const iconButtonBrowsers = getIconButtonBrowsers({
    matchedBrowserKey: matchedBrowser?.key,
    supportedBrowsers: SUPPORTED_BROWSERS,
    updateTarget,
  })

  const shouldBlock = !isSupported && !isBypassed
  const { setOptions } = useLoaderContext()

  useEffect(() => {
    if (!shouldBlock) {
      return
    }

    const reason = buildSentryReason({ updateTarget })

    Sentry.captureException(new Error(`${reason}: ${detectedBrowser}`), {
      extra: {
        minimumBrowser,
        osName,
        osVersion,
      },
      fingerprint: [browserName],
      level: 'warning',
      tags: {
        code: 'unsupported_browser',
      },
    })

    setTimeout(() => {
      setOptions({ loading: false })
    }, 1000)
  }, [shouldBlock])

  const handleProceed = (): void => {
    setBrowserGuardBypassed()
    setIsBypassed(true)
    Sentry.metrics({ attributes: validationResult, event: SentryEvent.BROWSER_GUARD_BYPASSED })
  }

  if (!shouldBlock) {
    return children
  }

  return (
    <UnsupportedBrowserView
      canBypass={canBypass}
      detectedBrowser={detectedBrowser}
      iconButtonBrowsers={iconButtonBrowsers}
      matchedBrowser={matchedBrowser}
      messageKeys={messageKeys}
      minimumBrowser={minimumBrowser}
      onProceed={handleProceed}
      safariBrowser={SAFARI_BROWSER}
      systemUpdateButton={systemUpdateButton}
      updateTarget={updateTarget}
      unsupported_feature={messageVariant === 'unsupported_feature'}
    />
  )
}
